import React from 'react'

import { Box, Tooltip, Button } from "@mui/material";

import AudioPlayer from "../../../../../AudioPlayer";
import ChatImage from "../../../../../ChatImage";
import ChatDocument from "../../../../../ChatDocument";
import ChatLocation from "../../../../../ChatLocation";
import ChatVideo from "../../../../../ChatVideo";
import api from '../../../../../../api';
import { getSession } from '../../../../../../auth';
import useNotification from "../../../../../Notification";
import useValidationError from '../../../../../ErrorValidation';

import MessageItem from "../../MessageItem";
import ChatStory from '../../../../../ChatStory';

import userPermissions from "../../../../../User/Permissions"
import { parseInteractiveToPlainText } from './KwikInteractivePlainParser';

const groups = userPermissions()

const isPending = (message) => {
    return 'status' in message && message.status === 'PENDING';
}

const isAudio = (message) => {
    return message.message.includes("wpp-audio");
}

const isStory = (message) => {
    return message.message.includes("[story]") &&
        message.message.includes("[/story]");
}

const isAudioWeb = (message) => {
    return message.message.includes("[audio]") &&
        message.message.includes("[/audio]");
}

const isImage = (message) => {
    return message.message.includes("wpp-image");
}

const isImageWeb = (message) => {
    return message.message.includes("[img]") &&
        message.message.includes("[/img]");
}

const isVideo = (message) => {
    return message.message.includes("wpp-video");
}

const isVideoWeb = (message) => {
    return message.message.includes("[video]") &&
        message.message.includes("[/video]");
}

const isDocument = (message) => {
    return message.message.includes("wpp-document");
}

const isDocumentWeb = (message) => {
    return message.message.includes("[url") &&
        message.message.includes("[/url]");
}

const isLocation = (message) => {
    return message.message.includes("wpp-location");
}

const isButtons = (message) => {
    return message.message.includes("[buttons");
}

const isCTA = (message) => {
    return message.message.includes("[/link")
}

const isSurvey = (message) => {
    return message.message.includes("[survey");
}

const isEmailInbox = (message) => {
    return message.message.includes("(emailinbox") || message.message.includes("(emailsubject") || message.message.includes("(emailattachment") || message.message.includes("(emailcc");
}

const isEmailSent = (message, activeChat) => {
    if (activeChat?.platform !== 'EMAIL') return false;

    return message.user_id !== 0 && message.user_id > 0 && !isEmailInbox(message);
}

const isSpecialMessage = (message, activeChat) => {
    const isSpecialMessage =
        isAudio(message) ||
        isAudioWeb(message) ||
        isImage(message) ||
        isImageWeb(message) ||
        isVideo(message) ||
        isVideoWeb(message) ||
        isDocument(message) ||
        isDocumentWeb(message) ||
        isLocation(message) ||
        isButtons(message) ||
        isCTA(message) ||
        isSurvey(message) ||
        isEmailInbox(message) ||
        isEmailSent(message, activeChat) ||
        isStory(message);

    return isSpecialMessage;
}

const isEmailCloseChatMessage = (message, activeChat) => {
    return activeChat.platform === 'EMAIL' && message.message === 'Chat encerrado pelo operador.';
}

const addInfo = (status, rmessage) => {
    let className = 'media-status';
    let message = '';

    switch (status) {
        case 'PENDING':
            className += ' media-pending';
            message = 'Aprovação pendente';
            break;
        case 'REJECTED':
            className += ' media-rejected';
            message = 'Rejeitada';
            break;
        case 'APPROVED':
            className += ' media-approved';
            message = 'Aprovada';
            break;
        default:
            break;
    }

    if (message) {
        return (
            <Box className={className}>
                {message}
            </Box>
        );
    }

    return (<div></div>);
}

const applyActionMsg = (message, index, status, reloadState, buttonDisabledSetter, enterpriseId, activeChat, parentSetter = () => { }) => {
    buttonDisabledSetter(true)
    parentSetter({ loaded: false })
    reloadState()
    api.patch(`/api/enterprises/${enterpriseId}/supervised_message/${message.id}/`, {
        chat: message.chat,
        status: status
    }, { timeout: 20000 }).then((res) => {
        if (res.status === 200) {
            activeChat.socket.send(JSON.stringify({
                channel: activeChat.channel ? activeChat.channel : activeChat.hash,
                message: JSON.stringify({ status: status, id: message.id }),
                user: -3
            }))

            switch (status) {
                case 'APPROVED':
                    useNotification(
                        'Sucesso!',
                        'Mensagem aprovada!',
                        'success'
                    )
                    break
                case 'REJECTED':
                    useNotification(
                        'Sucesso!',
                        'Mensagem rejeitada!',
                        'success'
                    );
                    break
                default:
                    break
            }

            message.status = status;
            parentSetter({ loaded: true })
            reloadState()
            return;
        }

        useValidationError(res.status, null);
        throw res;
    }).catch((err) => {
        parentSetter({ loaded: false })
        console.error(err);
    });
}

const addSupervisorAction = (message, index, reloadState, buttonDisabled, buttonDisabledSetter, enterpriseId, activeChat, parentSetter) => {
    return (
        <Box className="supervisor-action">
            <Tooltip title="Aprovar mídia" placement="bottom" disableInteractive>
                <Button
                    variant="contained"
                    color="success"
                    style={{ marginRight: '5px' }}
                    onClick={() => applyActionMsg(message, index, 'APPROVED', reloadState, buttonDisabledSetter, enterpriseId, activeChat, parentSetter)}
                    size="small"
                    disabled={buttonDisabled}>Aprovar</Button>
            </Tooltip>
            <Tooltip title="Rejeitar mídia" placement="bottom" disableInteractive>
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => applyActionMsg(message, index, 'REJECTED', reloadState, buttonDisabledSetter, enterpriseId, activeChat, parentSetter)}
                    size="small"
                    disabled={buttonDisabled}>Rejeitar</Button>
            </Tooltip>
        </Box>
    );
}


// Normalize text payloads (some messages may arrive as JSON string)
const normalizeRawText = (raw) => {
    if (typeof raw !== "string") return String(raw ?? "");
    const trimmed = raw.trim();

    if (trimmed.startsWith("{")) {
        try {
            const obj = JSON.parse(trimmed);
            if (obj && typeof obj.message === "string") return obj.message;
        } catch {
            // Keep original text
        }
    }

    return raw;
}

const getUrlFromLink = (message) => {
    const text = normalizeRawText(message);

    var regex = /\[url=(.*?)\]\[\/url\]/
    var match = regex.exec(text)
    if (match)
        return match[1]

    if (text.startsWith("[url=") && text.endsWith("]Baixar arquivo[/url]"))
        return text.replace("[url=", "").replace("]Baixar arquivo[/url]", "")

    return message
}

const toUnixSeconds = (dateStr) => {
    if (!dateStr) return Math.floor(Date.now() / 1000);
    // History format: "YYYY-MM-DD HH:mm:ss" -> make it ISO-like
    const d = new Date(dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T"));
    const ms = d.getTime();
    if (Number.isNaN(ms)) return Math.floor(Date.now() / 1000);
    return Math.floor(ms / 1000);
};

const renderDefault = (message, messages, index, accountcode, isManageCalls, queuesName, enterpriseId, activeChat, reloadState, buttonDisabled, buttonDisabledSetter, parentSetter, setReplying, updateMessage, supervisedQueues = []) => {
    if (!accountcode)
        accountcode = getSession()?.profile?.enterprise?.accountcode

    let shouldBlurMessage = false
    const enterpriseSettings = getSession()?.profile?.enterprise?.settings
    if (enterpriseSettings?.blur_other_queues_messages !== false) {
        if (!enterpriseSettings?.all_queue_administrator || !groups.isAdmin) {
            const messageQueue = message.dep_id || -1 // Recently added messages doesnt have a dep_id
            if (messageQueue !== -1) {
                const queues = [...(getSession().profile?.queues?.map((q) => q.id) || []), 0]
                if (groups.isSupervisor)
                    queues.push(...(supervisedQueues || []))
                shouldBlurMessage = !queues.includes(messageQueue)
            }
        }
    }

    return (
        <div className="message-item" key={index}>
            {isAudio(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <AudioPlayer user={message.user_id}
                        key={index}
                        src={(shouldBlurMessage)
                            ? "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA="
                            : "/api/api/whatsapp/audio/"
                            + accountcode
                            + "/"
                            + message.message.split(":")[1].split(";")[1]
                            + "/"
                            + message.message.split(":")[1].split(";")[0]
                            + "/"
                        }
                        message={message}
                        enterpriseId={enterpriseId}
                        updateMessage={updateMessage}
                    />
                </Box>
            }
            {isStory(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatStory
                        key={index}
                        message={message}
                        user={message.user_id}
                        queuesName={queuesName}
                        platform={activeChat?.platform}
                    />
                </Box>
            }
            {isAudioWeb(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <AudioPlayer user={message.user_id}
                        message={message}
                        key={index}
                        queuesName={queuesName}
                        platform={activeChat?.platform}
                        src={(shouldBlurMessage)
                            ? "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA="
                            : message.message.split("[audio]")[1].split("[/audio]")[0]
                        }
                        enterpriseId={enterpriseId}
                        updateMessage={updateMessage}
                    />
                </Box>
            }
            {isImage(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatImage user={message.user_id}
                        src={(shouldBlurMessage)
                            ? "https://picsum.photos/200"
                            : "/api/api/whatsapp/image/"
                            + accountcode
                            + "/"
                            + message.message.split(":")[1].split(";")[1]
                            + "/"
                            + message.message.split(":")[1].split(";")[0]
                            + "/"
                        }
                    />
                </Box>
            }
            {isImageWeb(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatImage user={message.user_id}
                        message={message}
                        key={index}
                        queuesName={queuesName}
                        platform={activeChat?.platform}
                        src={(shouldBlurMessage)
                            ? "https://picsum.photos/200"
                            : message.message.split("[img]")[1].split("[/img]")[0]
                        }
                    />
                </Box>
            }
            {isVideo(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatVideo user={message.user_id}
                        src={(shouldBlurMessage)
                            ? "https://www.w3schools.com/html/mov_bbb.mp4"
                            : "/api/api/whatsapp/video/"
                            + accountcode
                            + "/"
                            + message.message.split(":")[1].split(";")[1]
                            + "/"
                            + message.message.split(":")[1].split(";")[0]
                            + "/"
                        }
                    />
                </Box>
            }
            {isVideoWeb(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatVideo user={message.user_id}
                        message={message}
                        key={index}
                        queuesName={queuesName}
                        platform={activeChat?.platform}
                        src={(shouldBlurMessage)
                            ? "https://www.w3schools.com/html/mov_bbb.mp4"
                            : message.message.split("[video]")[1].split("[/video]")[0]
                        }
                    />
                </Box>
            }
            {isDocument(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatDocument user={message.user_id}
                        name={"Documento"}
                        message={message}
                        src={(shouldBlurMessage)
                            ? undefined
                            : "/api/api/whatsapp/document/"
                            + accountcode
                            + "/"
                            + message.message.split(":")[1].split(";")[1]
                            + "/"
                            + message.message.split(":")[1].split(";")[0]
                            + "/"
                        }
                    />
                </Box>
            }
            {isDocumentWeb(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatDocument user={message.user_id}
                        name={"Documento"}
                        message={message}
                        src={(shouldBlurMessage)
                            ? undefined
                            : getUrlFromLink(message.message)
                        }
                    />
                </Box>
            }
            {isLocation(message) &&
                <Box sx={{ filter: (shouldBlurMessage) ? "blur(5px)" : "none" }}>
                    <ChatLocation user={message.user_id}
                        name={"Localização"}
                        src={"https://www.google.com/maps/search/?api=1&query="
                            + message.message.split(":")[1].split(";")[0]
                            + ","
                            + message.message.split(":")[1].split(";")[1]
                        }
                    />
                </Box>
            }
            {isButtons(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="buttons"
                    queuesName={queuesName}
                    platform={activeChat?.platform}
                    activeChat={activeChat}
                    messages={messages}
                    messageIndex={index}
                    shouldBlurMessage={shouldBlurMessage}
                />
            }
            {isCTA(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="CTA"
                    queuesName={queuesName}
                    platform={activeChat?.platform}
                    activeChat={activeChat}
                    messages={messages}
                    messageIndex={index}
                    shouldBlurMessage={shouldBlurMessage}
                />
            }
            {isSurvey(message) && !message.message.includes('[survey][field') &&
                <>
                    <MessageItem key={index}
                        setReplying={setReplying}
                        message={message}
                        user={message.user_id}
                        type="survey"
                        platform={activeChat?.platform}
                        activeChat={activeChat}
                        messages={messages}
                        messageIndex={index}
                        shouldBlurMessage={shouldBlurMessage}
                    />
                </>
            }
            {!isSpecialMessage(message, activeChat) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    platform={activeChat?.platform}
                    receiveURL={activeChat?.queue?.settings?.can_receive_url}
                    activeChat={activeChat}
                    messages={messages}
                    messageIndex={index}
                    shouldBlurMessage={shouldBlurMessage}
                />
            }

            {isManageCalls && isPending(message) &&
                addSupervisorAction(message, index, reloadState, buttonDisabled, buttonDisabledSetter, enterpriseId, activeChat, parentSetter)
            }

            {!isManageCalls && isPending(message) &&
                addInfo(message.status, message, index)
            }

            {!isPending(message) &&
                addInfo(message.status, message, index)
            }
        </div>
    );
}

const renderSocialChannel = (message, index, accountcode, isManageCalls, queuesName, enterpriseId, activeChat, reloadState, buttonDisabled, buttonDisabledSetter, parentSetter, setReplying, setShowHtmlContent) => {
    let parsedMessage = message.message

    parsedMessage = parsedMessage.replace('[img]', '').replace('[/img]', '')
    parsedMessage = parsedMessage.replace('wpp-image:', '')
    parsedMessage = parsedMessage.replace('[video]', '').replace('[/video]', '')
    parsedMessage = parsedMessage.replace('wpp-video:', '')
    parsedMessage = parsedMessage.replace('[audio]', '').replace('[/audio]', '')
    parsedMessage = parsedMessage.replace('wpp-audio:', '')
    if (parsedMessage.includes("[url=")) {
        parsedMessage = parsedMessage.replace('[url=', '').replace('[/url]', '').slice(0, -1)
        if (parsedMessage.endsWith("]"))
            parsedMessage = parsedMessage.slice(0, -1)
    }
    parsedMessage = parsedMessage.replace('wpp-document:', '')

    return (
        <div className="message-item" key={index}>
            {isAudio(message) &&
                <AudioPlayer user={message.user_id}
                    src={parsedMessage}
                    key={index}
                />
            }
            {isAudioWeb(message) &&
                <AudioPlayer user={message.user_id}
                    src={parsedMessage}
                    key={index}
                />
            }
            {isImage(message) &&
                <ChatImage user={message.user_id}
                    src={parsedMessage}
                />
            }
            {isImageWeb(message) &&
                <ChatImage user={message.user_id}
                    src={parsedMessage}
                />
            }
            {isVideo(message) &&
                <ChatVideo user={message.user_id}
                    src={parsedMessage}
                />
            }
            {isVideoWeb(message) &&
                <ChatVideo user={message.user_id}
                    src={parsedMessage}
                />
            }
            {isDocument(message) &&
                <ChatDocument user={message.user_id}
                    name={"Documento"}
                    message={message}
                    src={parsedMessage}
                />
            }
            {isDocumentWeb(message) &&
                <ChatDocument user={message.user_id}
                    name={"Documento"}
                    src={parsedMessage}
                    message={message}
                    receiveURL={activeChat?.queue?.settings?.can_receive_url}
                />
            }
            {isButtons(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="buttons"
                    queuesName={queuesName}
                    platform={activeChat?.platform}
                    activeChat={activeChat}
                />
            }
            {isCTA(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="CTA"
                    queuesName={queuesName}
                    platform={activeChat?.platform}
                    activeChat={activeChat}
                />
            }
            {isSurvey(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="survey"
                    platform={activeChat?.platform}
                    activeChat={activeChat}
                />
            }
            {isEmailInbox(message) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="emailinbox"
                    platform={activeChat?.platform}
                    receiveURL={activeChat?.queue?.settings?.can_receive_url}
                    activeChat={activeChat}
                />
            }
            {isEmailSent(message, activeChat) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    type="emailsent"
                    platform={activeChat?.platform}
                    receiveURL={activeChat?.queue?.settings?.can_receive_url}
                    activeChat={activeChat}
                    emailTo={activeChat?.callerid}
                    emailSubject={activeChat?.subject || ''}
                    onShowHtml={(content) => setShowHtmlContent(content)}
                />
            }
            {!isSpecialMessage(message, activeChat) && !isEmailCloseChatMessage(message, activeChat) &&
                <MessageItem key={index}
                    setReplying={setReplying}
                    message={message}
                    user={message.user_id}
                    platform={activeChat?.platform}
                    receiveURL={activeChat?.queue?.settings?.can_receive_url}
                    activeChat={activeChat}
                />
            }

            {isManageCalls && isPending(message) &&
                addSupervisorAction(message, index, reloadState, buttonDisabled, buttonDisabledSetter, enterpriseId, activeChat, parentSetter)
            }

            {!isManageCalls && isPending(message) &&
                addInfo(message.status, message, index)
            }

            {!isPending(message) &&
                addInfo(message.status, message, index)
            }
        </div>
    );
}

// --- Plain renderer for History (no chat bubbles, no MessageItem) ---

const parseAttrs = (attrText = "") => {
    const attrs = {};
    const regex = /(\w+)=(".*?"|'.*?'|[^\s\]]+)/g;
    let m;

    while ((m = regex.exec(attrText)) !== null) {
        const key = m[1];
        let val = m[2] ?? "";
        if (
            (val.startsWith('"') && val.endsWith('"')) ||
            (val.startsWith("'") && val.endsWith("'"))
        ) {
            val = val.slice(1, -1);
        }
        attrs[key] = val;
    }

    return attrs;
};


const parseCTAToPlainText = (rawText) => {
    const text = normalizeRawText(rawText);

    // Best-effort: [link url=... ]Label[/link]
    const match = text.match(/\[link([^\]]*)\]([\s\S]*?)\[\/link\]/);
    if (!match) {
        // Fallback: strip link tags if they exist
        return text.replace(/\[\/?.*?link[^\]]*\]/g, "").trim() || text;
    }

    const attrs = parseAttrs(match[1] || "");
    const label = (match[2] || "").trim();
    const href = attrs.url || attrs.href || attrs.link || attrs.action || "";

    if (href && label) return `${label} (${href})`;
    if (href) return href;
    return label || text.replace(/\[\/?.*?link[^\]]*\]/g, "").trim() || text;
};

const stripSurveyToPlainText = (rawText) => {
    const text = normalizeRawText(rawText);

    // Remove survey tags and keep the human-readable content
    return text
        .replace(/\[\/??survey[^\]]*\]/g, "")
        .replace(/\[\/??field[^\]]*\]/g, "")
        .replace(/\[\/??option[^\]]*\]/g, "")
        .replace(/\[\/??label[^\]]*\]/g, "")
        .replace(/\[\/??value[^\]]*\]/g, "")
        .replace(/\s+/g, " ")
        .trim();
};

const parseStoryToPlainText = (rawText) => {
    const text = normalizeRawText(rawText);

    if (text.includes("[story]") && text.includes("[/story]")) {
        const parts = text.split("[story]");
        if (parts.length > 1) {
            const inside = parts[1].split("[/story]")[0];
            return (inside || "").trim() || "Story";
        }
    }
    return text.replace(/\[\/??story[^\]]*\]/g, "").trim() || "Story";
};

const stripEmailMarkupToPlainText = (rawText) => {
    const text = normalizeRawText(rawText);
    const formatValue = (value) => String(value || "").replace(/\s+/g, " ").trim();
    const formatAttachment = (rawAttachment) => {
        try {
            const attachment = JSON.parse(rawAttachment);
            const filename = formatValue(attachment?.filename || attachment?.name);
            return filename ? `\nAnexo: ${filename}\n` : "\nAnexo\n";
        } catch {
            return "\nAnexo\n";
        }
    };

    return text
        .replace(/\(emailinbox\)([\s\S]*?)\(\/emailinbox\)/gi, (match, inbox) => {
            const value = formatValue(inbox);
            return value ? `\nEmail: ${value}\n` : "";
        })
        .replace(/\(emailcc\)([\s\S]*?)\(\/emailcc\)/gi, (match, cc) => {
            const value = formatValue(cc);
            return value ? `\nCC: ${value}\n` : "";
        })
        .replace(/\(emailsubject\)([\s\S]*?)\(\/emailsubject\)/gi, (match, subject) => {
            const value = formatValue(subject);
            return value ? `\nAssunto: ${value}\n` : "";
        })
        .replace(/\(emailattachment\)([\s\S]*?)\(\/emailattachment\)/gi, (match, attachment) => formatAttachment(attachment))
        .replace(/\## - (.*?) - ##/g, "")
        .split("\n")
        .map(line => line.trim())
        .filter(Boolean)
        .join("\n");
};

const stripKwikMarkupToPlainText = (rawText) => {
    const text = normalizeRawText(rawText);
    const m = { message: text };

    if (isEmailInbox(m)) return stripEmailMarkupToPlainText(text);

    // Interactive types => use the same parsing logic as MessageItem
    if (isCTA(m)) return parseInteractiveToPlainText({ type: "CTA", message: text, lineBreak: "\n" });
    if (isButtons(m)) return parseInteractiveToPlainText({ type: "buttons", message: text, lineBreak: "\n" });
    if (isSurvey(m)) return parseInteractiveToPlainText({ type: "survey", message: text, lineBreak: "\n" });

    // Convert known tags to plain text (no markdown rendering)
    if (text.includes("[img]") && text.includes("[/img]")) return extractBetween(text, "[img]", "[/img]") || "Image";
    if (text.includes("[video]") && text.includes("[/video]")) return extractBetween(text, "[video]", "[/video]") || "Video";
    if (text.includes("[audio]") && text.includes("[/audio]")) return extractBetween(text, "[audio]", "[/audio]") || "Audio";

    if (text.includes("[url=") && text.includes("[/url]")) {
        const href = getUrlFromLink(text);
        return href || "Document";
    }

    // WPP prefixes => human label (use existing detectors to avoid duplicated logic)
    if (isAudio(m)) return "Audio";
    if (isVideo(m)) return "Video";
    if (isImage(m)) return "Image";
    if (isDocument(m)) return "Document";
    if (isLocation(m)) return "Location";

    return text;
};

const renderHistoryPlainContent = (rawText, { accountcode, user_id, date } = {}) => {
    const normalized = normalizeRawText(rawText);
    const msg = { message: normalized, user_id: user_id ?? 0, time: toUnixSeconds(date) };

    // Keep the same fallback behavior as chat
    if (!accountcode)
        accountcode = getSession()?.profile?.enterprise?.accountcode

    const safeText = (value) => (
        <span style={{ whiteSpace: "pre-wrap" }}>{value}</span>
    );

    // Media: render using the same components as chat, but without chat wrappers
    if (isAudio(msg)) {
        const payload = msg.message.split(":")[1];
        const uuid = payload?.split(";")[0];
        const folder = payload?.split(";")[1];
        if (!uuid || !folder || !accountcode) return safeText(stripKwikMarkupToPlainText(msg.message));

        const src = "/api/api/whatsapp/audio/" + accountcode + "/" + folder + "/" + uuid + "/";
        return <AudioPlayer user={user_id ?? 0} src={src} message={msg} showTimestamp={false} variant="history" />;
    }

    if (isAudioWeb(msg)) {
        const src = msg.message.split("[audio]")[1]?.split("[/audio]")[0];
        return src ? <AudioPlayer user={user_id ?? 0} src={src} message={msg} showTimestamp={false} variant="history" /> : safeText(stripKwikMarkupToPlainText(msg.message));
    }

    if (isVideo(msg)) {
        const payload = msg.message.split(":")[1];
        const uuid = payload?.split(";")[0];
        const folder = payload?.split(";")[1];
        if (!uuid || !folder || !accountcode) return safeText(stripKwikMarkupToPlainText(msg.message));

        const src = "/api/api/whatsapp/video/" + accountcode + "/" + folder + "/" + uuid + "/";
        return <ChatVideo user={user_id ?? 0} src={src} />;
    }

    if (isVideoWeb(msg)) {
        const src = msg.message.split("[video]")[1]?.split("[/video]")[0];
        return src ? <ChatVideo user={user_id ?? 0} src={src} /> : safeText(stripKwikMarkupToPlainText(msg.message));
    }

    if (isImage(msg)) {
        const payload = msg.message.split(":")[1];
        const uuid = payload?.split(";")[0];
        const folder = payload?.split(";")[1];
        if (!uuid || !folder || !accountcode) return safeText(stripKwikMarkupToPlainText(msg.message));

        const src = "/api/api/whatsapp/image/" + accountcode + "/" + folder + "/" + uuid + "/";
        return <ChatImage user={user_id ?? 0} src={src} />;
    }

    if (isImageWeb(msg)) {
        const src = msg.message.split("[img]")[1]?.split("[/img]")[0];
        return src ? <ChatImage user={user_id ?? 0} src={src} /> : safeText(stripKwikMarkupToPlainText(msg.message));
    }

    if (isDocument(msg)) {
        const payload = msg.message.split(":")[1];
        const uuid = payload?.split(";")[0];
        const folder = payload?.split(";")[1];
        if (!uuid || !folder || !accountcode) return safeText(stripKwikMarkupToPlainText(msg.message));

        const src = "/api/api/whatsapp/document/" + accountcode + "/" + folder + "/" + uuid + "/";
        return <ChatDocument user={user_id ?? 0} name={"Documento"} src={src} />;
    }

    if (isDocumentWeb(msg)) {
        const href = getUrlFromLink(msg.message);
        return href
            ? <ChatDocument user={user_id ?? 0} name={"Documento"} src={href} />
            : safeText(stripKwikMarkupToPlainText(msg.message));
    }

    if (isEmailInbox(msg)) {
        return safeText(stripEmailMarkupToPlainText(msg.message));
    }

    if (isLocation(msg)) {
        const payload = msg.message.split(":")[1];
        const lat = payload?.split(";")[0];
        const lon = payload?.split(";")[1];
        const href = (lat && lon)
            ? "https://www.google.com/maps/search/?api=1&query=" + lat + "," + lon
            : null;

        return href
            ? <ChatLocation user={user_id ?? 0} name={"Localização"} src={href} />
            : safeText(stripKwikMarkupToPlainText(msg.message));
    }

    // Special markup (buttons/CTA/survey/story/email) -> keep plain text
    if (isButtons(msg) || isCTA(msg) || isSurvey(msg) || isStory(msg) || isEmailInbox(msg) || isEmailSent(msg, { platform: "EMAIL" })) {
        return safeText(stripKwikMarkupToPlainText(msg.message));
    }

    // Default: raw text, no markdown
    return safeText(normalized);
};


export {
    isPending,
    isAudio,
    isStory,
    isAudioWeb,
    isImage,
    isImageWeb,
    isVideo,
    isVideoWeb,
    isDocument,
    isDocumentWeb,
    isLocation,
    isButtons,
    isCTA,
    isSurvey,
    isEmailInbox,
    isEmailSent,
    isSpecialMessage,
    isEmailCloseChatMessage,
    getUrlFromLink,
    renderDefault,
    renderSocialChannel,
    renderHistoryPlainContent,
};
